// Неигровые экраны: старт, лобби, подготовка, «передай телефон», конец партии.
// Preact-компоненты; наружу — прежние строковые функции (renderToString), пока app.ts рендерит через innerHTML.
import { h, Fragment } from 'preact';
import type { ComponentChildren, JSX } from 'preact';
import { render as toString } from 'preact-render-to-string';
import type { BossId, BotLevel, CorrGame, CorrMove, RatingLevel, DailyBoard, DailyFame, DailyInfo, DailyRow, FameBoard, Game, LeaderBoard, LeaderRow, Leaderboards, Player, PlayerId, Room, PresenceSnapshot, PresenceMode, PresencePlayer, RatingChange, Star, StarId, StarRow } from '../types.ts';
import { card, player, traitDef, setupDone } from '../engine/state.ts';
import { stepwiseSetup } from '../engine/rules.ts';
import * as Bot from '../engine/bot.ts';
import { H, winnersOf } from '../engine/game.ts';
import { cardV, CardChipV, Txt, isTraitRebalanced } from './cards.ts';
import { cmdProps } from './commands.ts';
import type { UiCommand } from './commands.ts';
import { Btn, Hint, Sub, cx } from './ui-kit.ts';
import type { V } from './ui-kit.ts';
import { TextV, hasCardMark } from './events.ts';
import type { FeedItem } from './events.ts';
import type { NetStatus, SetupUi, TopTab, UiState } from './state.ts';
import type { CorrState, DailyState, LeadersState, MyBossesState, PushState } from './store.ts';
import { BOSSES, BOSS_IDS } from '../engine/bosses.ts';
import { MUTATORS, PICKABLE_MUTATORS } from '../engine/mutators.ts';
import { DB } from '../engine/state.ts';
import { mutatorDefs } from '../engine/mutators.ts';
import { fmtDate } from './changelog.ts';
import { dailyMutatorLabel, dailyMutatorDesc, dailyBotsLabel, endsInLabel, marginLabel } from './daily-text.ts';
import { MAX_PLAYERS } from './state.ts';
/** Соперников-ботов против одного человека: стол не больше MAX_PLAYERS */
const MAX_BOTS = MAX_PLAYERS - 1;
import type { SaveData } from './storage.ts';
import type { SavedSession } from './net.ts';
import { isMuted } from './sound.ts';
import { GameAchV, gameAchItems } from './ach.ts';

const Screen = ({ cls, children }: { cls?: string; children?: ComponentChildren }): V => h('div', { class: cx('screen', cls) }, children);
const Panel = ({ cls, children }: { cls?: string; children?: ComponentChildren }): V => h('div', { class: cx('panel', cls) }, children);
const Row = ({ cls, children }: { cls?: string; children?: ComponentChildren }): V => h('div', { class: cx('row', cls) }, children);

/** Справка режима ребаланса — не кнопка в ряду с галочкой, а тихая ссылка рядом с ней */
const RebalanceInfoLink = (): V => h(Btn, { cls: 'link', id: 'btn-rebalance-info', c: { k: 'rebalanceInfo' } }, 'что изменено');

// ---------- присутствие: кто онлайн, открытые комнаты ----------
const MODE_ICON: Record<PresenceMode, string> = { idle: '🟢', hotseat: '📱', online: '🌐' };
/** Чем занят игрок — подпись к имени в списках */
export function modeLabel(p: PresencePlayer): string {
  if (p.mode === 'online') return p.room ? `в комнате ${p.room}` : 'в комнате';
  return p.mode === 'hotseat' ? 'играет хотсит' : 'на главном экране';
}
/** Сводка режимов: «в комнатах 2 · за хотситом 1 · на главном 3» */
export function modesSummary(p: PresenceSnapshot): string {
  const parts: string[] = [];
  if (p.modes.online) parts.push(`в комнатах ${p.modes.online}`);
  if (p.modes.hotseat) parts.push(`за хотситом ${p.modes.hotseat}`);
  if (p.modes.idle) parts.push(`на главном ${p.modes.idle}`);
  return parts.join(' · ');
}

/** Сколько онлайн — от себя: «Ты + 2 онлайн» / «Только ты онлайн» (голое «1 онлайн» смущало: один — это ты сам) */
export function onlineCount(p: PresenceSnapshot, myId: string): string {
  const others = Math.max(0, p.online - (p.players.some((x) => x.id === myId) ? 1 : 0));
  return others ? `Ты + ${others} онлайн` : 'Только ты онлайн';
}

/** Колокольчик с числом непрочитанных и кнопка «что нового» — шапка стартового экрана и лобби;
 *  compact — только колокольчик в углу (экран «передай телефон», итог); me — плашка профиля с именем (хаб) */
const SocialBar = ({ unread, compact = false, me }: { unread: number; compact?: boolean; me?: string }): V =>
  h('div', { class: cx('socialbar', compact && 'compact', me != null && 'with-profile') },
    compact ? null : h(Btn, { cls: 'ghost', id: 'btn-changelog', c: { k: 'changelog' } }, '📋 Что нового'),
    me != null ? h(Btn, { cls: cx('ghost profile-chip', !me && 'nameless'), id: 'btn-profile', title: 'Профиль: имя и устройства', c: { k: 'profile' } }, '👤 ', h('span', { class: 'pc-name' }, me || 'Назваться')) : null,
    h(Btn, { cls: cx('ghost bell', unread > 0 && 'has-unread'), id: 'btn-inbox', title: 'Уведомления', c: { k: 'inbox' } }, '🔔', unread > 0 ? h('span', { class: 'bell-n' }, unread) : null));

/** Кто сейчас онлайн: счётчик, режимы и имена; myId — этот браузер помечается «(ты)» */
export function PresencePanel({ p, myId }: { p: PresenceSnapshot | null; myId: string }): V {
  if (!p) return h(Hint, { cls: 'hint presence-off' }, 'Нет связи с сервером — кто онлайн и открытые комнаты появятся, когда она будет.');
  return h('div', { class: 'presence' },
    h(Row, { cls: 'nowrap' }, h('span', { class: 'online-dot' }), h('b', null, onlineCount(p, myId)), h(Sub, { cls: 'sub' }, modesSummary(p))),
    h('div', { class: 'who-list' }, p.players.map((pl) =>
      h('span', { class: cx('who', `who-${pl.mode}`), title: modeLabel(pl) }, `${MODE_ICON[pl.mode]} ${pl.name}${pl.id === myId ? ' (ты)' : ''}`))));
}

/** Открытые комнаты: одна кнопка — и ты в лобби */
export function LobbyList({ p }: { p: PresenceSnapshot | null }): V {
  if (!p) return null;
  return h(Fragment, null,
    h('h3', null, 'Открытые комнаты'),
    p.lobbies.length
      ? p.lobbies.map((l) => h(Row, { cls: 'lobby-item nowrap' },
          h('div', { class: 'lobby-info' },
            h('b', null, `Комната ${l.code}`), l.rebalance ? ' ⚖️' : '',
            h(Sub, null, `хост ${l.host} · ${l.players.join(', ')} (${l.n}/${l.max})`)),
          h(Btn, { cls: 'primary', id: `btn-lobby-${l.code}`, c: { k: 'joinLobby', code: l.code }, disabled: l.n >= l.max }, 'Войти')))
      : h(Hint, null, 'Открытых комнат нет. Создай свою — её увидят все, кто онлайн.'));
}

// ---------- старт ----------
export interface StartProps {
  ui: UiState;
  saved: SaveData | null;
  online: SavedSession | null;
  /** снимок присутствия (null — сервера нет) */
  presence: PresenceSnapshot | null;
  /** непрочитанных уведомлений */
  unread: number;
  /** id этого клиента (помечается «(ты)» в списке онлайн) */
  myId: string;
  /** рейтинги с сервера (экран «Рейтинги») */
  leaders?: LeadersState;
  /** сид дня с сервера (плитка на хабе и экран «Сид дня») */
  daily?: DailyState;
  /** мои встречи с боссами (плитка и экран дуэли) */
  bosses?: MyBossesState;
  /** партии по переписке (плитка и экран) и уведомления этого устройства */
  corr?: CorrState;
  push?: PushState;
}
/** Шапка экрана режима: назад (на хаб или куда скажут) и заголовок */
const HomeHead = ({ title, back }: { title: string; back?: { id: string; c: UiCommand } }): V =>
  h('div', { class: 'home-head' },
    h(Btn, { cls: 'ghost back', id: back?.id ?? 'btn-home-back', c: back?.c ?? { k: 'home', tab: 'hub' } }, '‹ Назад'),
    h('h1', null, title));

/** Крупная плитка режима на хабе: значок, название, одна строка пояснения */
const ModeTile = ({ id, icon, title, sub, c, cls }: { id: string; icon: string; title: string; sub: ComponentChildren; c: UiCommand; cls?: string }): V =>
  h('button', { class: cx('mode-tile', cls), id, ...cmdProps(c) },
    h('span', { class: 'mt-icon' }, icon),
    h('span', { class: 'mt-body' }, h('span', { class: 'mt-title' }, title), h('span', { class: 'mt-sub' }, sub)),
    h('span', { class: 'mt-go' }, '›'));

/** Незаконченные партии — первое, что видно на хабе: продолжить важнее, чем начать новую */
function ResumeCards({ saved, online }: { saved: SaveData | null; online: SavedSession | null }): V {
  const hot = saved && saved.G.phase !== 'ended' ? saved : null;
  if (!hot && !online) return null;
  return h('div', { class: 'resume' },
    online ? h(Panel, { cls: 'resume-card' },
      h('div', { class: 'rc-info' }, h('b', null, `🌐 Комната ${online.code}`), h(Sub, null, `ты — ${online.name}`)),
      h(Row, { cls: 'nowrap' },
        h(Btn, { cls: 'primary', id: 'btn-online-resume', c: { k: 'resume' } }, 'Вернуться'),
        h(Btn, { cls: 'ghost', id: 'btn-forget', title: 'Забыть комнату', c: { k: 'forget' } }, '✕'))) : null,
    hot ? h(Panel, { cls: 'resume-card' },
      h('div', { class: 'rc-info' }, h('b', null, 'Прошлая партия'), h(Sub, null, hot.G.players.map((p) => p.name).join(', '))),
      h(Row, { cls: 'nowrap' },
        h(Btn, { cls: 'primary', id: 'btn-continue', c: { k: 'continue' } }, 'Продолжить'),
        h(Btn, { cls: 'ghost', id: 'btn-delete', title: 'Удалить сохранённую партию', c: { k: 'delete' } }, '✕'))) : null);
}

/** Строка про онлайн на плитке: сколько людей и открытых комнат (без сервера — честно, что его нет) */
function onlineSub(p: PresenceSnapshot | null, myId: string): string {
  if (!p) return 'Каждый со своего телефона';
  const rooms = p.lobbies.length;
  return `🟢 ${onlineCount(p, myId)}${rooms ? ` · открытых комнат: ${rooms}` : ''}`;
}

/** Подпись плитки «Сид дня»: мутатор дня и моя попытка; без сервера — что это такое */
function dailySub(d: DailyState | undefined, saved: SaveData | null): string {
  const info = d?.data;
  if (!info) return d?.error ? 'Один стол на всех, одна попытка в день · нужна сеть' : 'Один стол на всех, одна попытка в день';
  const mut = dailyMutatorLabel(info.spec);
  const me = info.me;
  if (me.status === 'done') return `${mut} · ты ${me.place ?? '?'}-й из ${info.today.rows.length}, отрыв ${marginLabel(me.margin)}`;
  if (me.status === 'playing' || (saved && saved.G.daily === info.spec.day && saved.G.phase !== 'ended')) return `${mut} · попытка начата`;
  return `${mut} · сыграли: ${info.today.rows.length}`;
}

/** Подпись плитки «Дуэль с боссом»: сколько боссов побеждено */
function duelSub(b: MyBossesState | undefined): string {
  const beaten = (b?.data ?? []).filter((x) => x.wins > 0).length;
  return b?.data && b.data.length ? `Один на один · побеждено ${beaten} из ${BOSS_IDS.length}` : `Один на один с одним из ${BOSS_IDS.length} боссов`;
}

function Hub({ ui, saved, online, presence, unread, myId, daily, bosses, corr }: StartProps): V {
  return h(Screen, { cls: 'start hub' },
    h(SocialBar, { unread, compact: true, me: ui.onlineName }),
    h('div', { class: 'logo' }, h('h1', null, 'Крутагидон'), h(Sub, null, 'Экстремально острый чипсихоз')),
    h(ResumeCards, { saved, online }),
    h('div', { class: 'mode-tiles' },
      h(ModeTile, { id: 'btn-home-bots', icon: '⚔️', title: 'Против ботов', sub: quickSub(ui), c: { k: 'home', tab: 'bots' }, cls: saved || online ? '' : 'hot' }),
      h(ModeTile, { id: 'btn-home-daily', icon: '📅', title: 'Сид дня', sub: dailySub(daily, saved), c: { k: 'home', tab: 'daily' } }),
      h(ModeTile, { id: 'btn-home-duel', icon: '🥊', title: 'Дуэль с боссом', sub: duelSub(bosses), c: { k: 'home', tab: 'duel' } }),
      h(ModeTile, { id: 'btn-home-online', icon: '🌐', title: 'Онлайн с друзьями', sub: onlineSub(presence, myId), c: { k: 'home', tab: 'online' } }),
      h(ModeTile, { id: 'btn-home-corr', icon: '✉️', title: 'Бокс по переписке', sub: corrSub(corr), c: { k: 'home', tab: 'corr' }, cls: corr?.data?.games.some((g) => g.myMove) ? 'hot' : '' }),
      h(ModeTile, { id: 'btn-home-hotseat', icon: '📱', title: 'Своя партия', sub: 'Компания за одним телефоном, боты любой сложности', c: { k: 'home', tab: 'hotseat' } }),
      h(ModeTile, { id: 'btn-home-top', icon: '🏆', title: 'Рейтинги', sub: 'Кто чем выделяется: серии, рекорды, боссы — и все номинации', c: { k: 'home', tab: 'top' } })),
    h('div', { class: 'home-foot' },
      h(Btn, { cls: 'ghost pill', id: 'btn-changelog', c: { k: 'changelog' } }, '📋 Что нового'),
      h(Btn, { cls: 'ghost pill', id: 'btn-mute', c: { k: 'mute' } }, isMuted() ? '🔇 Звук выключен' : '🔊 Звук включён')),
    h(Hint, { cls: 'hint home-rules' }, 'По официальным правилам v1.1: 20 жизней, 4 жетона дохлых колдунов на игрока.'));
}

const botsWord = (n: number): string => (n === 1 ? 'бот' : n < 5 ? 'бота' : 'ботов');
const LEVEL_PLURAL: Record<RatingLevel, string> = { easy: 'лёгкие', normal: 'средние', hard: 'сложные', cheat: 'нечестные', boss: 'боссы', duel: 'дуэли', mut: 'с мутаторами' };
/** Значок номинации: уровень ботов, корона боссов, перчатка дуэлей, кубик мутаторов */
export const levelEmoji = (l: RatingLevel): string => (l === 'boss' ? '👑' : l === 'duel' ? '🥊' : l === 'mut' ? '🎲' : Bot.LEVEL_INFO[l].emoji);
export const levelLabel = (l: RatingLevel): string => LEVEL_PLURAL[l];
/** Подпись плитки «Против ботов»: прошлый выбор, с ним и откроется экран */
function quickSub(ui: UiState): string {
  const lvl = Bot.LEVEL_INFO[ui.quickLevel];
  const base = ui.quickBots === 1 ? `1 бот · ${lvl.emoji} ${lvl.label}` : `${ui.quickBots} ${botsWord(ui.quickBots)} · ${lvl.emoji} ${LEVEL_PLURAL[ui.quickLevel]}`;
  const muts = ui.mutators.length ? ` · ${ui.mutators.map((m) => MUTATORS[m].emoji).join('')}` : '';
  return `${ui.quickBoss ? `${base} · 👑 с боссом` : base}${muts}`;
}

/** Мгновенные боты и ребаланс — общие для партий с ботами и своей партии */
const GameSettings = ({ ui }: { ui: UiState }): V =>
  h(Panel, { cls: 'col' },
    h('label', { class: 'row nowrap' },
      h('input', { type: 'checkbox', id: 'cb-instant', checked: ui.instantBots, ...cmdProps({ k: 'instantToggle' }) }),
      ' Боты ходят мгновенно, перед твоим ходом — сводка'),
    h(Row, { cls: 'nowrap' },
      h('label', { class: 'row nowrap', style: 'flex:1' },
        h('input', { type: 'checkbox', id: 'cb-rebalance', checked: ui.rebalance, ...cmdProps({ k: 'rebalanceToggle' }) }),
        ' ⚖️ Ребаланс имбовых карт'),
      h(RebalanceInfoLink, null)),
    h(Row, { cls: 'nowrap mut-head' }, h('span', { class: 'mut-title', style: 'flex:1' }, '🎲 Мутаторы'), h(Btn, { cls: 'link', id: 'btn-mutators-help', c: { k: 'mutatorsHelp' } }, 'что это')),
    h('div', { class: 'mut-chips' }, PICKABLE_MUTATORS.map((id) =>
      h(Btn, { cls: cx('chip', ui.mutators.includes(id) && 'on'), id: `btn-mut-${id}`, title: MUTATORS[id].desc, c: { k: 'mutToggle', id } }, `${MUTATORS[id].emoji} ${MUTATORS[id].name}`))),
    ui.mutators.length ? h(Sub, null, 'С мутаторами против сложных ботов и сильнее — номинация «🎲 с мутаторами», против лёгких и средних — таблица своего уровня.') : null);

const LEVELS: readonly BotLevel[] = ['easy', 'normal', 'hard', 'cheat'];

function BotsHome({ ui }: { ui: UiState }): V {
  const lvl = Bot.LEVEL_INFO[ui.quickLevel];
  return h(Screen, { cls: 'start mode-screen' },
    h(HomeHead, { title: 'Против ботов' }),
    h('h2', null, 'Сколько соперников'),
    h('div', { class: 'seg seg-4' }, Array.from({ length: MAX_BOTS }, (_, i) => i + 1).map((n) =>
      h(Btn, { cls: cx('seg-btn', ui.quickBots === n && 'on'), id: `btn-quick-n-${n}`, c: { k: 'quickN', n } }, String(n)))),
    h('h2', null, 'Уровень'),
    h('div', { class: 'seg seg-2x2' }, LEVELS.map((l) =>
      h(Btn, { cls: cx('seg-btn', ui.quickLevel === l && 'on', l), id: `btn-quick-lvl-${l}`, c: { k: 'quickLevel', level: l } },
        h('span', { class: 'seg-emoji' }, Bot.LEVEL_INFO[l].emoji), ' ', Bot.LEVEL_INFO[l].label))),
    h(Hint, null, `${lvl.emoji} ${lvl.label[0]!.toUpperCase()}${lvl.label.slice(1)}: ${lvl.hint}.`),
    h(Panel, { cls: 'col mt' },
      h('label', { class: 'row nowrap' },
        h('input', { type: 'checkbox', id: 'cb-quick-boss', checked: ui.quickBoss, ...cmdProps({ k: 'quickBossToggle' }) }),
        ' 👑 Один из ботов — босс'),
      h(Sub, null, 'Босс — сложный бот с лицом колдуна, своим фамильяром и одной уникальной пассивкой; какой — решит сид партии. Партии с боссом идут в отдельную номинацию «👑 боссы». Один бот-босс — это дуэль по своим правилам; выбрать соперника — в «🥊 Дуэли с боссом».')),
    h('h2', null, 'Настройки'),
    h(GameSettings, { ui }),
    h(Btn, { cls: 'primary big-btn wide mt', id: 'btn-quick', c: { k: 'quickBots' } }, 'В бой'),
    h(Hint, { cls: 'hint center' }, 'Разные уровни за одним столом и друзья рядом — в «Своей партии».'));
}

function OnlineHome({ ui, online, presence, myId, unread }: StartProps): V {
  return h(Screen, { cls: 'start mode-screen has-bell' },
    h(SocialBar, { unread, compact: true }),
    h(HomeHead, { title: 'Онлайн' }),
    h(ResumeCards, { saved: null, online }),
    h('label', { class: 'field' }, h('span', { class: 'field-label' }, 'Твоё имя'),
      h('input', { type: 'text', id: 'online-name', placeholder: 'Как тебя звать', value: ui.onlineName, maxlength: 16 })),
    h(Btn, { cls: 'primary big-btn wide', id: 'btn-create', c: { k: 'create' } }, 'Создать комнату'),
    h('div', { class: 'join-row' },
      h('input', { type: 'text', id: 'join-code', placeholder: 'Код комнаты', value: ui.joinCode, maxlength: 6, style: 'text-transform:uppercase' }),
      h(Btn, { id: 'btn-join', c: { k: 'join' } }, 'Войти')),
    h(Panel, { cls: 'col' }, h(LobbyList, { p: presence })),
    h('h3', null, 'Кто онлайн'),
    h(PresencePanel, { p: presence, myId }),
    h(Hint, { cls: 'hint mt' }, 'Комнату сразу видят все, кто онлайн. Друзей можно позвать и кодом или ссылкой из лобби; когда все зашли — хост жмёт «Начать».'));
}

function HotseatHome({ ui }: { ui: UiState }): V {
  const allBots = ui.bots.slice(0, ui.nPlayers).every(Boolean);
  const botTitle = (i: number): string => {
    const lvl = ui.bots[i];
    if (!lvl) return 'человек — нажми, чтобы посадить бота';
    const info = Bot.LEVEL_INFO[lvl];
    return `${info.label} бот: ${info.hint} — нажми для следующего уровня${lvl === 'cheat' ? ' (вернёт человека)' : ''}`;
  };
  return h(Screen, { cls: 'start mode-screen' },
    h(HomeHead, { title: 'Своя партия' }),
    h('div', { class: 'section-head' }, h('h2', null, 'Игроки'),
      h('div', { class: 'stepper small' }, h(Btn, { id: 'btn-np-minus', c: { k: 'npMinus' } }, '−'), h('b', { id: 'np' }, ui.nPlayers), h(Btn, { id: 'btn-np-plus', c: { k: 'npPlus' } }, '+'))),
    h(Panel, { cls: 'col' },
      Array.from({ length: ui.nPlayers }, (_, i) => h(Row, { cls: 'nowrap' },
        h('input', { type: 'text', 'data-name': i, placeholder: ui.bots[i] ? `Бот ${i + 1}` : `Имя игрока ${i + 1}`, value: ui.names[i] ?? '', maxlength: 16 }),
        h(Btn, { cls: cx('bot-toggle', ui.bots[i] && `on ${ui.bots[i]}`), title: botTitle(i), c: { k: 'botToggle', i } }, ui.bots[i] ? Bot.LEVEL_INFO[ui.bots[i]].emoji : '🤖'))),
      h(Hint, null, 'Кнопка справа сажает бота: 🐣 лёгкий → 🤖 средний → 😎 сложный → 😈 нечестный (видит руки) → снова человек.')),
    h('h2', null, 'Настройки'),
    h(GameSettings, { ui }),
    h(Btn, { cls: 'primary big-btn wide mt', id: 'btn-new', c: { k: 'newGame' }, disabled: allBots }, allBots ? 'Нужен хотя бы один человек' : 'Начать партию'));
}

// ---------- игра по переписке ----------
/** Чего ждут от меня в партии по переписке: коротко — плашка строки, подробно — её подпись */
const CORR_MOVE: Record<CorrMove, string> = { setup: 'твой выбор', third: 'твой выбор', turn: 'твой ход', prompt: 'ответь' };
const CORR_MOVE_LONG: Record<CorrMove, string> = { setup: 'выбери жетон и фамильяра', third: 'выбери третьего фамильяра', turn: 'ходи', prompt: 'нужен твой ответ' };

/** Подпись плитки «По переписке»: сколько партий ждут меня */
function corrSub(c: CorrState | undefined): string {
  const games = c?.data?.games ?? [];
  const mine = games.filter((g) => g.myMove).length;
  if (mine) return `🔔 Ждут твоего хода: ${mine} ${plural(mine, 'партия', 'партии', 'партий')}`;
  const live = games.filter((g) => g.status !== 'ended').length;
  if (live) return `${live} ${plural(live, 'партия', 'партии', 'партий')} · ждём соперников`;
  return 'Бей, когда удобно, — на ринг позовёт уведомление';
}

/** Уведомления этого устройства: включены — строка с проверкой; нет — объяснение и кнопка (без них в партию не войти) */
function PushPanel({ push }: { push: PushState }): V {
  const err = push.error ? h('div', { class: 'hint err-text', id: 'push-error' }, push.error) : null;
  if (push.status === 'on') {
    return h(Panel, { cls: 'push-panel on' },
      h(Row, { cls: 'nowrap' }, h('span', { style: 'flex:1' }, '🔔 Уведомления на этом устройстве включены'),
        h(Btn, { cls: 'link', id: 'btn-push-test', c: { k: 'pushTest' }, disabled: push.busy }, 'проверить')),
      err);
  }
  const why: Record<Exclude<PushState['status'], 'on'>, string> = {
    unknown: 'Проверяем уведомления…',
    off: 'Нужны уведомления: партия по переписке идёт днями, и когда до тебя дойдёт ход, она позовёт тебя уведомлением. Без них в партию не войти.',
    denied: 'Уведомления для игры запрещены в настройках браузера. Разреши их для этого сайта (значок слева от адреса → Уведомления) и обнови страницу.',
    iosBrowser: 'На iPhone уведомления приходят только приложению: в Safari нажми «Поделиться» → «На экран „Домой“» и открой игру оттуда.',
    unsupported: 'Этот браузер не умеет системные уведомления. Открой игру в Chrome или установи её как приложение.',
  };
  return h(Panel, { cls: 'push-panel off col' },
    h('div', null, '🔕 ', why[push.status]),
    push.status === 'off' ? h(Btn, { cls: 'primary', id: 'btn-push-enable', c: { k: 'pushEnable' }, disabled: push.busy }, push.busy ? 'Включаем…' : '🔔 Включить уведомления') : null,
    err);
}

/** Моя партия в списке: чего ждут (от меня — выделено), состав; тап — открыть */
function CorrGameV({ g }: { g: CorrGame }): V {
  const waiting = g.seats.filter((s) => s.waiting).map((s) => s.name);
  const names = g.seats.map((s) => s.name).join(', ');
  const icon = g.myMove ? '🔔' : g.status === 'lobby' ? '⏳' : g.status === 'ended' ? '🏁' : '✉️';
  const sub = g.status === 'lobby'
    ? `набор ${g.seats.length} из ${g.size}: ${names}`
    : g.status === 'ended'
      ? `окончена — посмотреть итог · ${names}`
      : g.myMove ? `${CORR_MOVE_LONG[g.myMove]} · ${names}` : `ждём: ${waiting.join(', ') || '…'}`;
  return h(Btn, { cls: cx('game-row corr-game', g.myMove && 'my-move'), id: `btn-corr-${g.code}`, c: { k: 'corrOpen', code: g.code } },
    h('span', { class: 'gr-ic' }, icon),
    h('div', { class: 'lb-main' },
      h('div', { class: 'gr-head' }, h('b', null, `Партия ${g.code}`), g.myMove ? h('span', { class: 'corr-move' }, CORR_MOVE[g.myMove]) : null),
      h('div', { class: 'sub' }, sub)),
    h('span', { class: 'gr-go' }, '›'));
}

function CorrHome({ ui, corr, push }: StartProps): V {
  const c = corr?.data ?? null;
  const pushOn = push?.status === 'on';
  const lobbies = [...(c?.lobbies ?? [])].sort((a, b) => Number(b.code === ui.corrFocus) - Number(a.code === ui.corrFocus));
  return h(Screen, { cls: 'start mode-screen corr-screen' },
    h(HomeHead, { title: 'Бокс по переписке' }),
    h(Hint, null, 'Партия идёт днями: ходишь, когда удобно, а когда до тебя доходит очередь — приходит уведомление. Партия начинается сама, как только наберутся все игроки.'),
    push ? h(PushPanel, { push }) : null,
    h('h2', null, 'Мои партии'),
    c
      ? c.games.length
        ? h('div', { class: 'game-list' }, c.games.map((g) => h(CorrGameV, { g })))
        : h(Hint, null, 'Пока нет партий — создай свою или войди в открытую.')
      : h(Hint, null, corr?.error ? 'Нет связи с сервером — партии по переписке живут на нём.' : 'Загрузка…'),
    h('h2', null, 'Новая партия'),
    h('label', { class: 'field' }, h('span', { class: 'field-label' }, 'Твоё имя'),
      h('input', { type: 'text', id: 'online-name', placeholder: 'Как тебя звать', value: ui.onlineName, maxlength: 16 })),
    h('div', { class: 'field-label' }, 'Игроков за столом'),
    h('div', { class: 'seg seg-4' }, [2, 3, 4, 5].map((n) =>
      h(Btn, { cls: cx('seg-btn', ui.corrSize === n && 'on'), id: `btn-corr-size-${n}`, c: { k: 'corrSize', n } }, String(n)))),
    h(Btn, { cls: 'primary big-btn wide mt', id: 'btn-corr-create', c: { k: 'corrCreate' }, disabled: !pushOn || !!corr?.busy }, 'Создать партию'),
    h(Hint, null, 'Набор сразу виден всем в «Открытых партиях»; позвать друга можно ссылкой из комнаты. Свободные места можно отдать ботам.'),
    h('h2', null, 'Открытые партии'),
    lobbies.length
      ? h(Panel, null, lobbies.map((l) => h(Row, { cls: cx('lobby-item nowrap', l.code === ui.corrFocus && 'focus') },
          h('div', { class: 'lobby-info' },
            h('b', null, `Партия ${l.code}`),
            h(Sub, null, `хост ${l.host} · ${l.players.join(', ')} (${l.n} из ${l.size})`)),
          h(Btn, { cls: 'primary', id: `btn-corr-join-${l.code}`, c: { k: 'corrJoin', code: l.code }, disabled: !pushOn }, 'Войти'))))
      : h(Hint, null, c ? 'Открытых наборов нет — создай свой.' : ''),
    h(Btn, { cls: 'link', id: 'btn-corr-refresh', c: { k: 'corrRefresh' } }, corr?.loading ? 'Обновляем…' : 'Обновить список'));
}

// ---------- рейтинги ----------
export const MEDAL = ['🥇', '🥈', '🥉'];
const plural = (n: number, one: string, few: string, many: string): string => {
  const d = n % 10, dd = n % 100;
  return d === 1 && dd !== 11 ? one : d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? few : many;
};
export const gamesWord = (n: number): string => `${n} ${plural(n, 'партия', 'партии', 'партий')}`;
export const winsWord = (n: number): string => plural(n, 'победа', 'победы', 'побед');

/** Строка таблицы: место, имя и подробности, справа — главное число номинации (победы или рейтинг) */
const LeaderRowV = ({ r, people }: { r: LeaderRow; people: boolean }): V =>
  h(Btn, { cls: cx('lb-row', r.me && 'me', r.rank <= 3 && `top${r.rank}`), title: `Профиль: ${r.name}`, c: r.me ? { k: 'profile' } : { k: 'playerProfile', id: r.id } },
    h('span', { class: 'lb-rank' }, r.rank <= 3 ? MEDAL[r.rank - 1] : r.rank),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, r.name, r.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
      h('div', { class: 'sub' }, people
        ? `${gamesWord(r.games)} · ${r.wins} ${winsWord(r.wins)} · рекорд ${r.best}★`
        : `${gamesWord(r.games)} · ${Math.round((100 * r.wins) / Math.max(1, r.games))}% побед · рекорд ${r.best}★`)),
    h('div', { class: 'lb-val' },
      h('b', null, people ? r.rating ?? '—' : r.wins),
      h('span', { class: 'sub' }, people ? 'рейтинг' : winsWord(r.wins))));

function BoardV({ b, people }: { b: LeaderBoard; people: boolean }): V {
  if (!b.rows.length) {
    return h(Hint, { cls: 'hint lb-empty' }, people
      ? 'Пока никого. Сыграй онлайн с друзьями — и откроешь таблицу.'
      : 'Пока никого на этом уровне. Выиграй первым — и займёшь вершину.');
  }
  return h('div', { class: 'lb-list' },
    b.rows.map((r) => h(LeaderRowV, { r, people })),
    b.me ? h(Fragment, null, h('div', { class: 'lb-gap' }, '···'), h(LeaderRowV, { r: b.me, people })) : null);
}

/** Номинация экрана рейтингов: таблица «против ботов», вечный зал сида дня или «против людей» */
type Nom = Exclude<TopTab, 'all' | StarId>;
/** Порядок номинаций в витрине: уровни ботов от самого играемого, особые режимы, люди */
const NOMS: readonly Nom[] = ['hard', 'cheat', 'normal', 'easy', 'boss', 'duel', 'mut', 'daily', 'people'];
const NOM_TITLE: Record<Nom, string> = {
  hard: 'Сложные боты', cheat: 'Нечестные боты', normal: 'Средние боты', easy: 'Лёгкие боты',
  boss: 'Против боссов', duel: 'Дуэли с боссами', mut: 'С мутаторами', daily: 'Сид дня', people: 'Против людей',
};
const nomEmoji = (n: Nom): string => (n === 'people' ? '🌐' : n === 'daily' ? '📅' : levelEmoji(n));
const LEVEL_HINT = 'Партии, где ты один против ботов: «Против ботов», «Своя партия» с одним человеком или онлайн-комната с ботами. Смешанный стол — по среднему уровню ботов (лёгкий + сложный = средний). Партии с ручными правками не в счёт.';
const NOM_HINT: Record<Nom, string> = {
  easy: LEVEL_HINT, normal: LEVEL_HINT, hard: LEVEL_HINT, cheat: LEVEL_HINT,
  people: 'Онлайн-партии, где за столом двое людей и больше (боты не мешают). Рейтинг Эло: старт 1000, растёт за каждого человека, которого ты обошёл по месту, — сильнее, если он выше тебя в рейтинге.',
  boss: 'Партии, где ты один против ботов и за столом был хотя бы один босс: уровни остальных ботов не важны. Классический сид дня в день босса тоже здесь. Партии с ручными правками не в счёт.',
  duel: 'Дуэли один на один с боссом по правилам дуэли (отстающий по очкам берёт карту, на 10 ПО и больше — две). Партии с ручными правками не в счёт.',
  mut: 'Партии с мутаторами против сложных ботов и сильнее или с боссом за столом, в том числе сид дня с мутатором. С лёгкими и средними ботами мутаторы идут в таблицу своего уровня: по замерам они не меняют, насколько исход решает умение.',
  daily: 'Вечный зал сида дня: сколько раз ты был первым в таблице дня — с лучшим отрывом от лучшего бота. День засчитывается, когда закончился.',
};
const daysWord = (n: number): string => plural(n, 'день', 'дня', 'дней');

/** Звёзды: значок, название, что считается; число строкой (карточка), число и подпись (таблица), коротко (для «ты») */
interface StarDef { icon: string; title: string; hint: string; value: (r: StarRow) => string; num: (r: StarRow) => string; unit: (r: StarRow) => string; short: (r: StarRow) => string }
const STARS: Record<StarId, StarDef> = {
  streak: { icon: '🔥', title: 'Серия', hint: 'побед подряд против сложных и сильнее',
    value: (r) => `${r.value} ${plural(r.value, 'победа', 'победы', 'побед')} подряд`, num: (r) => `${r.value}`, unit: () => 'подряд', short: (r) => `${r.value} подряд` },
  sniper: { icon: '🎯', title: 'Меткость', hint: 'доля побед против сложных и сильнее, от 20 партий',
    value: (r) => `${r.value}% побед из ${r.of ?? 0}`, num: (r) => `${r.value}%`, unit: (r) => `из ${r.of ?? 0}`, short: (r) => `${r.value}%` },
  record: { icon: '⭐', title: 'Рекорд', hint: 'больше всех ★ за одну партию',
    value: (r) => `${r.value}★ за партию`, num: (r) => `${r.value}★`, unit: () => 'за партию', short: (r) => `${r.value}★` },
  hunter: { icon: '👑', title: 'Охотник на боссов', hint: 'разных побеждённых боссов',
    value: (r) => `${r.value} из ${BOSS_IDS.length} боссов`, num: (r) => `${r.value}`, unit: () => `из ${BOSS_IDS.length}`, short: (r) => `${r.value} из ${BOSS_IDS.length}` },
  week: { icon: '📈', title: 'Форма недели', hint: 'побед за 7 дней против сложных и сильнее',
    value: (r) => `${r.value} ${winsWord(r.value)} за неделю`, num: (r) => `${r.value}`, unit: (r) => winsWord(r.value), short: (r) => `${r.value} ${winsWord(r.value)}` },
  marathon: { icon: '🏃', title: 'Марафонец', hint: 'больше всех партий в рейтингах',
    value: (r) => gamesWord(r.value), num: (r) => `${r.value}`, unit: (r) => plural(r.value, 'партия', 'партии', 'партий'), short: (r) => gamesWord(r.value) },
};
const STAR_IDS = Object.keys(STARS) as StarId[];
const isStar = (t: TopTab): t is StarId => (STAR_IDS as readonly string[]).includes(t);
const myStarRow = (s: Star): StarRow | undefined => s.rows.find((r) => r.me) ?? s.me;

/** Лучшее место игрока и его медали — для «Кто чем выделяется» */
interface Standout { id: string; name: string; me: boolean; icon: string; title: string; text: string; rank: number; total: number; medals: number[] }

/**
 * Кто чем выделяется: у каждого игрока — его лучшее место среди звёзд и номинаций (медаль считается, когда в
 * категории хотя бы трое — первое место из двух ещё не выделяет). Так видно не только общего лидера, но и того, кто
 * силён в своём: охотник на боссов, король сида дня, лучшая форма недели.
 */
export function standouts(data: Leaderboards): Standout[] {
  const by = new Map<string, Standout>();
  const add = (id: string, name: string, me: boolean, icon: string, title: string, text: string, rank: number, total: number): void => {
    if (rank > 3 || total < 3) return;
    let s = by.get(id);
    if (!s) {
      s = { id, name, me, icon, title, text, rank, total, medals: [0, 0, 0] };
      by.set(id, s);
    } else if (rank < s.rank || (rank === s.rank && total > s.total)) {
      Object.assign(s, { icon, title, text, rank, total });
    }
    s.medals[rank - 1]!++;
  };
  for (const s of data.stars ?? []) {
    const d = STARS[s.id];
    if (!d) continue;
    for (const r of s.rows) add(r.id, r.name, !!r.me, d.icon, d.title, d.value(r), r.rank, s.total);
  }
  for (const n of NOMS) {
    if (n === 'daily') {
      const b = data.daily;
      for (const r of b?.rows ?? []) add(r.id, r.name, !!r.me, nomEmoji(n), NOM_TITLE[n], `${r.daysWon} ${daysWord(r.daysWon)} первым`, r.rank, b!.total);
      continue;
    }
    const b = n === 'people' ? data.people : data.bots[n];
    for (const r of b?.rows ?? []) {
      add(r.id, r.name, !!r.me, nomEmoji(n), NOM_TITLE[n], n === 'people' ? `рейтинг ${r.rating ?? '—'}` : `${r.wins} ${winsWord(r.wins)}`, r.rank, b!.total);
    }
  }
  return [...by.values()].sort((a, b) => a.rank - b.rank || b.medals[0]! - a.medals[0]! || b.medals[1]! - a.medals[1]! || b.total - a.total || a.name.localeCompare(b.name));
}

const STANDOUT_TOP = 8;

/** Строка «кто чем выделяется»: медаль лучшего места, имя, категория; ниже — все медали. Тап — профиль */
const StandoutV = ({ s }: { s: Standout }): V => {
  const tally = s.medals.map((n, i) => (n ? `${MEDAL[i]}${n}` : '')).filter(Boolean).join(' ');
  return h(Btn, { cls: cx('lb-row standout', s.me && 'me', `top${s.rank}`), title: `Профиль: ${s.name}`, c: s.me ? { k: 'profile' } : { k: 'playerProfile', id: s.id } },
    h('span', { class: 'lb-rank' }, MEDAL[s.rank - 1]),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, s.name, s.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
      h('div', { class: 'sub so-best' }, `${s.icon} ${s.title}: ${s.text}`),
      h('div', { class: 'sub' }, `медали: ${tally}`)),
    h('span', { class: 'gr-go' }, '›'));
};

/** Звезда: кто лидирует и чем; внизу — моё место в ней или что она считает. Тап — таблица звезды */
function StarCardV({ s, leader }: { s: Star; leader: StarRow }): V {
  const d = STARS[s.id];
  const me = myStarRow(s);
  return h(Btn, { cls: cx('star-card', leader.me && 'mine'), id: `btn-star-${s.id}`, title: `${d.title}: ${d.hint}`, c: { k: 'topTab', tab: s.id } },
    h('div', { class: 'sc-head' }, h('span', { class: 'sc-ic' }, d.icon), h('span', { class: 'sc-title' }, d.title)),
    h('div', { class: 'sc-name' }, leader.name, leader.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
    h('div', { class: 'sc-val' }, d.value(leader)),
    h('div', { class: 'sc-foot' }, me && !leader.me ? `ты: ${me.rank}-й · ${d.short(me)}` : d.hint));
}

/** Лидер и моё место в номинации — для строки витрины */
function nomSummary(n: Nom, data: Leaderboards): { leader: string | null; leaderMe: boolean; myRank: number | null; total: number } {
  if (n === 'daily') {
    const b: FameBoard = data.daily ?? { rows: [], total: 0 };
    const top = b.rows[0];
    const me = b.rows.find((r) => r.me) ?? b.me;
    return { leader: top ? `${top.name} · ${top.daysWon} ${daysWord(top.daysWon)}` : null, leaderMe: !!top?.me, myRank: me?.rank ?? null, total: b.total };
  }
  const b: LeaderBoard = (n === 'people' ? data.people : data.bots[n]) ?? { rows: [], total: 0 };
  const top = b.rows[0];
  const me = b.rows.find((r) => r.me) ?? b.me;
  const val = top ? (n === 'people' ? `${top.rating ?? '—'}` : `${top.wins} ${winsWord(top.wins)}`) : '';
  return { leader: top ? `${top.name} · ${val}` : null, leaderMe: !!top?.me, myRank: me?.rank ?? null, total: b.total };
}

/** Строка номинации в витрине: значок, название, лидер; справа — моё место; тап — таблица */
function NomRowV({ n, data }: { n: Nom; data: Leaderboards }): V {
  const s = nomSummary(n, data);
  return h(Btn, { cls: 'nom-row', id: `btn-top-${n}`, title: NOM_TITLE[n], c: { k: 'topTab', tab: n } },
    h('span', { class: 'nom-ic' }, nomEmoji(n)),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, NOM_TITLE[n]),
      h('div', { class: 'sub' }, s.leader ? `🥇 ${s.leader}${s.leaderMe ? ' (ты)' : ''}` : 'пока никого — займи первым')),
    h('div', { class: 'nom-me' }, s.myRank != null
      ? h(Fragment, null, h('b', null, s.myRank <= 3 ? MEDAL[s.myRank - 1] : `${s.myRank}-й`), h('span', { class: 'sub' }, `из ${s.total}`))
      : s.total ? h('span', { class: 'sub' }, `${s.total} ${plural(s.total, 'игрок', 'игрока', 'игроков')}`) : null),
    h('span', { class: 'gr-go' }, '›'));
}

/** Витрина рейтингов: кто чем выделяется, звёзды и все номинации, по строке на каждую */
function TopOverview({ ui, data }: { ui: UiState; data: Leaderboards }): V {
  const stars = (data.stars ?? []).flatMap((s) => (s.rows[0] && STARS[s.id] ? [{ s, leader: s.rows[0] }] : []));
  const outs = standouts(data);
  const mine = outs.findIndex((s) => s.me);
  const shown = outs.slice(0, STANDOUT_TOP);
  if (mine >= STANDOUT_TOP) shown.push(outs[mine]!);
  return h(Fragment, null,
    shown.length ? h(Fragment, null,
      h('h2', null, 'Кто чем выделяется'),
      h('div', { class: 'lb-list so-list' }, shown.map((s) => h(StandoutV, { s })))) : null,
    stars.length ? h(Fragment, null,
      h('h2', null, 'Звёзды'),
      h('div', { class: 'star-grid' }, stars.map(({ s, leader }) => h(StarCardV, { s, leader })))) : null,
    h('h2', null, 'Номинации'),
    h('div', { class: 'lb-list nom-list' }, NOMS.map((n) => h(NomRowV, { n, data }))),
    h('label', { class: 'field mt' }, h('span', { class: 'field-label' }, 'Твоё имя в рейтингах'),
      h('input', { type: 'text', id: 'online-name', placeholder: 'Как тебя звать', value: ui.onlineName, maxlength: 16 })),
    h(Btn, { cls: 'link', id: 'btn-top-profile', c: { k: 'profile', tab: 'devices' } }, 'Играешь с нескольких устройств? Свяжи их'),
    h(Hint, null, 'Медаль — место с первого по третье в звезде или номинации, где хотя бы трое игроков. Серия, меткость и форма недели считаются по партиям против сложных ботов и сильнее. Вне рейтингов — только партии с ручными правками и партии нескольких людей за одним телефоном: там не различить, кто есть кто.'));
}

/** Строка таблицы звезды: место, имя, число звезды */
const StarRowV = ({ r, d }: { r: StarRow; d: StarDef }): V =>
  h(Btn, { cls: cx('lb-row', r.me && 'me', r.rank <= 3 && `top${r.rank}`), title: `Профиль: ${r.name}`, c: r.me ? { k: 'profile' } : { k: 'playerProfile', id: r.id } },
    h('span', { class: 'lb-rank' }, r.rank <= 3 ? MEDAL[r.rank - 1] : r.rank),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, r.name, r.me ? h('span', { class: 'lb-you' }, ' (ты)') : null)),
    h('div', { class: 'lb-val' }, h('b', null, d.num(r)), h('span', { class: 'sub' }, d.unit(r))));

/** Таблица одной номинации или звезды */
function NomBoardV({ n, data }: { n: Exclude<TopTab, 'all'>; data: Leaderboards }): V {
  if (isStar(n)) {
    const s = (data.stars ?? []).find((x) => x.id === n);
    if (!s?.rows.length) return h(Hint, { cls: 'hint lb-empty' }, 'Пока никого — стань первым.');
    const d = STARS[n];
    return h('div', { class: 'lb-list' },
      s.rows.map((r) => h(StarRowV, { r, d })),
      s.me ? h(Fragment, null, h('div', { class: 'lb-gap' }, '···'), h(StarRowV, { r: s.me, d })) : null);
  }
  if (n === 'daily') {
    const b = data.daily;
    if (!b?.rows.length) return h(Hint, { cls: 'hint lb-empty' }, 'Пока никого. Сыграй сид дня — когда день закончится, он попадёт в зал.');
    return h('div', { class: 'lb-list' },
      b.rows.map((r) => h(FameRowV, { r })),
      b.me ? h(Fragment, null, h('div', { class: 'lb-gap' }, '···'), h(FameRowV, { r: b.me })) : null);
  }
  const people = n === 'people';
  return h(BoardV, { b: (people ? data.people : data.bots[n]) ?? { rows: [], total: 0 }, people });
}

function TopHome({ ui, leaders }: StartProps): V {
  const tab: TopTab = ui.topTab;
  const data = leaders?.data ?? null;
  const status = data ? null
    : leaders?.error
      ? h(Hint, { cls: 'hint lb-empty' }, 'Рейтинги хранятся на сервере, а связи с ним сейчас нет. Загляни, когда появится сеть.')
      : h(Hint, { cls: 'hint lb-empty' }, 'Загрузка…');
  if (tab === 'all') {
    return h(Screen, { cls: 'start mode-screen top-screen' },
      h(HomeHead, { title: 'Рейтинги' }),
      data ? h(TopOverview, { ui, data }) : status);
  }
  const title = isStar(tab) ? `${STARS[tab].icon} ${STARS[tab].title}` : `${nomEmoji(tab)} ${NOM_TITLE[tab]}`;
  const hint = isStar(tab)
    ? `${STARS[tab].title}: ${STARS[tab].hint}.${tab === 'record' || tab === 'marathon' ? ' Считаются все партии в рейтингах.' : ' Сложные и сильнее — сложные и нечестные боты, боссы, дуэли и мутаторы.'}`
    : NOM_HINT[tab];
  return h(Screen, { cls: 'start mode-screen top-screen' },
    h(HomeHead, { title, back: { id: 'btn-top-back', c: { k: 'topTab', tab: 'all' } } }),
    data ? h(NomBoardV, { n: tab, data }) : status,
    h(Hint, null, hint));
}

// ---------- дуэль с боссом ----------
/** Плитка босса: портрет, имя, пассивка и мой счёт против него; тап — дуэль */
const BossTileV = ({ id, stat }: { id: BossId; stat?: { games: number; wins: number } }): V =>
  h(Btn, { cls: cx('boss-tile', stat?.wins ? 'won' : stat ? 'met' : ''), id: `btn-duel-${id}`, title: `Дуэль: ${BOSSES[id].name}`, c: { k: 'duelStart', boss: id } },
    h('img', { class: 'bt-face', src: DB.cards[BOSSES[id].familiar]?.art ?? '', alt: '' }),
    h('div', { class: 'bt-main' },
      h('div', { class: 'bt-name' }, BOSSES[id].short, stat?.wins ? h('span', { class: 'bt-crown' }, ' ✓') : null),
      h('div', { class: 'bt-text' }, BOSSES[id].text),
      h('div', { class: 'bt-rec' }, stat ? `побед ${stat.wins} из ${stat.games}` : 'ещё не встречались')));

function DuelHome({ bosses }: StartProps): V {
  const by = new Map((bosses?.data ?? []).map((s) => [s.id, s]));
  const beaten = (bosses?.data ?? []).filter((s) => s.wins > 0).length;
  return h(Screen, { cls: 'start mode-screen duel-screen' },
    h(HomeHead, { title: 'Дуэль с боссом' }),
    h(Hint, null, 'Один на один с боссом — сложным ботом с лицом колдуна и уникальной пассивкой. Правило дуэли: в начале хода отстающий по очкам берёт карту, а отстающий на 10 ПО и больше — две, так что отыграться можно до конца. Победы идут в номинацию «🥊 дуэли» и в коллекцию боссов профиля.'),
    bosses?.data ? h(Sub, { cls: 'sub duel-progress' }, `Побеждено ${beaten} из ${BOSS_IDS.length}${beaten === BOSS_IDS.length ? ' — все десять!' : ''}`)
      : bosses?.error ? h(Sub, { cls: 'sub duel-progress' }, 'Нет связи с сервером — счёт против боссов не виден, играть можно.') : null,
    h('div', { class: 'boss-grid' }, BOSS_IDS.map((id) => h(BossTileV, { id, ...(by.get(id) ? { stat: by.get(id)! } : {}) }))));
}

// ---------- сид дня ----------
/** Строка таблицы дня: место, имя, очки; справа — отрыв от лучшего бота */
const DailyRowV = ({ r, replay }: { r: DailyRow; replay: boolean }): V =>
  h(Btn, { cls: cx('lb-row daily-row', r.me && 'me', r.rank <= 3 && `top${r.rank}`, r.won && 'won'), title: replay && r.game ? `Смотреть партию: ${r.name}` : `Профиль: ${r.name}`,
    c: replay && r.game ? { k: 'dailyGame', game: r.game, name: r.name, score: r.score, won: r.won } : r.me ? { k: 'profile' } : { k: 'playerProfile', id: r.id } },
    h('span', { class: 'lb-rank' }, r.rank <= 3 ? MEDAL[r.rank - 1] : r.rank),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, r.name, r.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
      h('div', { class: 'sub' }, `${r.won ? '🏆 победа' : 'поражение'} · ${r.score}★${replay && r.game ? ' · ▶ смотреть' : ''}`)),
    h('div', { class: 'lb-val' },
      h('b', { class: cx('dl-margin', r.margin > 0 ? 'up' : r.margin < 0 ? 'down' : '') }, marginLabel(r.margin)),
      h('span', { class: 'sub' }, 'отрыв')));

function DailyBoardV({ b, replay, empty }: { b: DailyBoard; replay: boolean; empty: string }): V {
  return h(Fragment, null,
    b.rows.length ? h('div', { class: 'lb-list' }, b.rows.map((r) => h(DailyRowV, { r, replay }))) : h(Hint, { cls: 'hint lb-empty' }, empty),
    b.unfinished ? h(Sub, { cls: 'sub center mt-s' }, `Начали и не доиграли: ${b.unfinished}`) : null);
}

const FameRowV = ({ r }: { r: DailyFame }): V =>
  h(Btn, { cls: cx('lb-row', r.me && 'me', r.rank <= 3 && `top${r.rank}`), title: `Профиль: ${r.name}`, c: r.me ? { k: 'profile' } : { k: 'playerProfile', id: r.id } },
    h('span', { class: 'lb-rank' }, r.rank <= 3 ? MEDAL[r.rank - 1] : r.rank),
    h('div', { class: 'lb-main' },
      h('div', { class: 'lb-name' }, r.name, r.me ? h('span', { class: 'lb-you' }, ' (ты)') : null),
      h('div', { class: 'sub' }, `дней сыграно: ${r.days} · лучший отрыв ${marginLabel(r.bestMargin)}`)),
    h('div', { class: 'lb-val' }, h('b', null, r.daysWon), h('span', { class: 'sub' }, daysWord(r.daysWon))));

/** Кнопка/статус попытки: продолжить сохранённую, начать, начата на другом устройстве, сыграна */
function DailyAction({ info, saved, d }: { info: DailyInfo; saved: SaveData | null; d: DailyState }): V {
  const me = info.me;
  const local = !!saved && saved.G.daily === info.spec.day && saved.G.phase !== 'ended';
  if (local) return h(Btn, { cls: 'primary big-btn wide mt', id: 'btn-daily-continue', c: { k: 'continue' } }, 'Продолжить попытку');
  if (me.status === 'done') {
    return h(Panel, { cls: 'daily-me mt' },
      h('div', { class: 'rc-head' }, h('span', null, me.won ? '🏆 Победа' : 'Сыграно'), h('span', { class: 'sub' }, `${me.score}★`)),
      h('div', { class: 'rc-line' }, 'Место сегодня: ', h('b', null, me.place ?? '—'), ` из ${info.today.rows.length} · отрыв `, h('b', null, marginLabel(me.margin))),
      h(Sub, null, `Следующий сид ${endsInLabel(info.endsIn)}`));
  }
  if (me.status === 'playing') return h(Hint, { cls: 'hint center mt' }, 'Попытка дня уже начата на другом устройстве — доиграй её там. Одна попытка в день.');
  return h(Fragment, null,
    h(Btn, { cls: 'primary big-btn wide mt', id: 'btn-daily-start', c: { k: 'dailyStart' }, disabled: d.starting }, d.starting ? 'Готовим стол…' : 'Играть'),
    d.startErr ? h(Hint, { cls: 'hint center err' }, d.startErr) : null);
}

function DailyHome({ ui, saved, daily }: StartProps): V {
  const d: DailyState = daily ?? { data: null, loading: false, error: false, startErr: '', starting: false };
  const info = d.data;
  return h(Screen, { cls: 'start mode-screen daily-screen' },
    h(HomeHead, { title: 'Сид дня' }),
    !info
      ? h(Hint, { cls: 'hint lb-empty' }, d.error
        ? 'Сид дня живёт на сервере, а связи с ним сейчас нет. Загляни, когда появится сеть.'
        : 'Загрузка…')
      : h(Fragment, null,
        h(Panel, { cls: 'daily-spec' },
          h('div', { class: 'ds-date' }, fmtDate(info.spec.day), h('span', { class: 'sub' }, `Новый сид ${endsInLabel(info.endsIn)}`)),
          h('div', { class: 'ds-mut' }, dailyMutatorLabel(info.spec)),
          h(Sub, null, dailyMutatorDesc(info.spec)),
          h('div', { class: 'ds-bots' }, h('b', null, `${info.spec.bots.length} сложных бота: `), dailyBotsLabel(info.spec))),
        h(DailyAction, { info, saved, d }),
        h('h2', null, 'Сегодня'),
        h(DailyBoardV, { b: info.today, replay: false, empty: 'Сегодня ещё никто не доиграл. Будь первым.' }),
        info.yesterday ? h(Fragment, null, h('h2', null, `Вчера, ${fmtDate(info.yesterday.day)}`), h(DailyBoardV, { b: info.yesterday, replay: true, empty: 'Вчера никто не доиграл.' })) : null,
        h('h2', null, 'Зал славы'),
        info.fame.length
          ? h('div', { class: 'lb-list' }, info.fame.map((r) => h(FameRowV, { r })))
          : h(Hint, { cls: 'hint lb-empty' }, 'Зал пока пуст: первый выигранный день откроет его.'),
        h('label', { class: 'field mt' }, h('span', { class: 'field-label' }, 'Твоё имя в таблицах'),
          h('input', { type: 'text', id: 'online-name', placeholder: 'Как тебя звать', value: ui.onlineName, maxlength: 16 })),
        h(Hint, null, 'Каждый день один стол на всех: тот же сид, тот же мутатор, те же три сложных бота. Одна попытка в день на аккаунт — начатая партия считается сыгранной. Место в таблице — по отрыву от лучшего бота, при равенстве — по очкам. День сменяется в полночь по Москве; записи партий дня открываются для просмотра назавтра. Классический сид дня идёт и в рейтинг «против ботов» (в день босса — в «боссы»), день с мутатором — только сюда.')));
}

/** Экран итога партии дня: место в таблице дня (когда запись дошла до сервера) */
export function DailyResultV({ G, d }: { G: Game; d: DailyState }): V {
  const info = d.data;
  if (!G.daily) return null;
  if (!info || info.spec.day !== G.daily) return h(Hint, { cls: 'hint center mt' }, d.loading ? '📅 Считаем место в таблице дня…' : d.error ? '📅 Сид дня: нет связи с сервером — результат зачтётся, когда запись дойдёт.' : null);
  const me = info.me;
  return h(Panel, { cls: 'rating-change daily-result mt' },
    h('div', { class: 'rc-head' }, h('span', null, '📅 Сид дня'), h('span', { class: 'sub' }, `${fmtDate(info.spec.day)} · ${dailyMutatorLabel(info.spec)}`)),
    me.status === 'done'
      ? h('div', { class: 'rc-line' }, 'Место сегодня: ', h('b', null, me.place ?? '—'), ` из ${info.today.rows.length} · отрыв от лучшего бота `, h('b', null, marginLabel(me.margin)))
      : h('div', { class: 'rc-line' }, d.loading ? 'Считаем место…' : 'Результат ещё не зачтён — запись отправится, когда появится сеть.'),
    h(Btn, { cls: 'link', id: 'btn-end-daily', c: { k: 'endToDaily' } }, 'Открыть таблицу дня'));
}

export function StartScreen(props: StartProps): V {
  if (props.ui.home === 'daily') return h(DailyHome, props);
  if (props.ui.home === 'duel') return h(DuelHome, props);
  if (props.ui.home === 'top') return h(TopHome, props);
  if (props.ui.home === 'bots') return h(BotsHome, { ui: props.ui });
  if (props.ui.home === 'online') return h(OnlineHome, props);
  if (props.ui.home === 'hotseat') return h(HotseatHome, { ui: props.ui });
  if (props.ui.home === 'corr') return h(CorrHome, props);
  return h(Hub, props);
}
export function startScreenHtml(ui: UiState, saved: SaveData | null, online: SavedSession | null, presence: PresenceSnapshot | null = null, unread = 0, myId = '', leaders?: LeadersState, daily?: DailyState, bosses?: MyBossesState, corr?: CorrState, push?: PushState): string {
  return toString(h(StartScreen, { ui, saved, online, presence, unread, myId, ...(leaders ? { leaders } : {}), ...(daily ? { daily } : {}), ...(bosses ? { bosses } : {}), ...(corr ? { corr } : {}), ...(push ? { push } : {}) }));
}

/** Экран-заглушка «передай телефон» / ожидание: крупный заголовок и одна-две кнопки */
const Pass = ({ cls, children }: { cls?: string; children?: ComponentChildren }): V => h('div', { class: cx('pass', cls) }, children);

export function connectingV(): JSX.Element {
  return h(Pass, null, h('div', { class: 'big' }, 'Подключение…'), h(Btn, { cls: 'ghost', id: 'btn-leave', c: { k: 'leave' } }, 'Отмена'));
}
export const connectingHtml = (): string => toString(connectingV());

// ---------- лобби ----------
export interface LobbyProps {
  r: Room;
  myPid: PlayerId | null;
  status: NetStatus;
  /** снимок присутствия — кого можно позвать (null — сервера присутствия нет) */
  presence?: PresenceSnapshot | null;
  /** id этого клиента: себя не зовём */
  myId?: string;
  unread?: number;
}
/** Кого позвать: все онлайн, кроме меня и тех, кто уже в этой комнате */
export function inviteCandidates(p: PresenceSnapshot | null | undefined, code: string, myId: string): PresencePlayer[] {
  return (p?.players ?? []).filter((pl) => pl.id !== myId && pl.room !== code);
}
export function Lobby({ r, myPid, status, presence = null, myId = '', unread = 0 }: LobbyProps): V {
  const isHost = r.host === myPid;
  const corr = !!r.corr;
  // по переписке ссылка ведёт на экран партий: войти можно только с уведомлениями
  const link = `${location.origin}${location.pathname}?${corr ? 'corr' : 'room'}=${r.code}`;
  const badge = (t: string): V => h('span', { class: 'badge' }, t);
  const seats = corr ? r.size ?? 5 : 5;
  const full = r.players.length >= seats;
  const open = r.open !== false;
  const candidates = inviteCandidates(presence, r.code, myId);
  return h(Screen, null,
    h(SocialBar, { unread }),
    h('h1', null, corr ? 'Бокс по переписке' : 'Комната'),
    corr ? h(Hint, { cls: 'hint center' }, `Набор ${r.players.length} из ${seats}: партия начнётся сама, как только наберутся все, — каждому придёт уведомление выбрать жетон и фамильяра.`) : null,
    h(Panel, { cls: 'center' },
      h(Sub, null, corr ? 'Код партии' : 'Код комнаты'), h('div', { class: 'code' }, r.code),
      h(Sub, { cls: 'sub break' }, link),
      h(Btn, { cls: 'ghost mt', id: 'btn-copy', c: { k: 'copy', link } }, 'Скопировать ссылку')),
    // открытая комната: видна в списке у всех онлайн; хост может закрыть — тогда вход только по коду/ссылке
    corr
      ? h(Sub, { cls: 'sub' }, open ? '🌍 Набор виден всем в «Бокс по переписке → Открытые партии»' : '🔒 Закрытый набор: вход только по ссылке')
      : isHost
      ? h('label', { class: 'row nowrap' },
          h('input', { type: 'checkbox', id: 'cb-room-open', checked: open, ...cmdProps({ k: 'roomOpen', on: !open }) }),
          ' 🌍 Открытая комната: видна в списке у всех, кто онлайн')
      : h(Sub, { cls: 'sub' }, open ? '🌍 Открытая комната: видна в списке у всех, кто онлайн' : '🔒 Закрытая комната: вход только по коду или ссылке'),
    // режим ребаланса: хост включает в лобби, остальные видят; справка «что изменено» доступна всем
    h(Row, { cls: 'nowrap' },
      isHost
        ? h('label', { class: 'row nowrap', style: 'flex:1' },
            h('input', { type: 'checkbox', id: 'cb-room-rebalance', checked: !!r.rebalance, ...cmdProps({ k: 'roomRebalance', on: !r.rebalance }) }),
            ' ⚖️ Режим ребаланса: правленые цены и эффекты имбовых карт')
        : h(Sub, { cls: 'sub', style: 'flex:1' }, r.rebalance ? '⚖️ Партия в режиме ребаланса (правленые цены и эффекты)' : 'Обычные правила (хост может включить режим ребаланса)'),
      h(RebalanceInfoLink, null)),
    h('h2', null, `Игроки (${r.players.length}/${seats})`),
    h(Panel, null, r.players.map((p) => h(Row, { cls: 'lobby-row' },
      `${p.bot ? '🤖' : p.connected ? '🟢' : '⚪'} `, h('b', null, p.name), ' ',
      p.bot ? badge(p.botLevel && Bot.LEVEL_INFO[p.botLevel] ? `${Bot.LEVEL_INFO[p.botLevel].emoji} ${Bot.LEVEL_INFO[p.botLevel].label} бот` : 'бот') : null, ' ',
      p.pid === r.host ? badge('хост') : null, ' ',
      p.pid === myPid ? badge('это ты') : null))),
    h(Row, { cls: 'mt' },
      isHost ? h(Fragment, null,
        corr ? null : h(Btn, { cls: 'primary', id: 'btn-start', c: { k: 'start' }, disabled: r.players.length < 2 }, 'Начать игру'),
        (['easy', 'normal', 'hard', 'cheat'] as const).map((lvl) => h(Btn, { id: `btn-addbot-${lvl}`, title: `${Bot.LEVEL_INFO[lvl].label} бот: ${Bot.LEVEL_INFO[lvl].hint}`, c: { k: 'addBot', level: lvl }, disabled: full }, `+ ${Bot.LEVEL_INFO[lvl].emoji} ${Bot.LEVEL_INFO[lvl].label[0]!.toUpperCase()}${Bot.LEVEL_INFO[lvl].label.slice(1)}`)))
        : h(Hint, null, corr ? 'Ждём, пока наберутся все…' : 'Ждём, пока хост начнёт игру…'),
      h(Btn, { cls: 'ghost', id: 'btn-leave', c: { k: 'leave' } }, corr ? '‹ К партиям' : 'Выйти')),
    corr && isHost ? h(Hint, null, 'Боты займут свободные места — когда мест не останется, партия начнётся.') : null,
    // позвать в игру: любой, кто онлайн, — и тот, кто сейчас за хотситом (приглашение придёт ему уведомлением)
    h('h2', null, 'Позвать в игру'),
    presence
      ? candidates.length
        ? h(Panel, null, candidates.map((pl) => h(Row, { cls: 'lobby-row nowrap' },
            h('div', { class: 'lobby-info' }, `${MODE_ICON[pl.mode]} `, h('b', null, pl.name), ' ', h('span', { class: 'sub' }, modeLabel(pl))),
            h(Btn, { id: `btn-invite-${pl.id}`, c: { k: 'invite', id: pl.id }, disabled: full }, 'Позвать'))))
        : h(Hint, null, `Больше никого онлайн нет${presence.online > 1 ? ' — остальные уже здесь' : ''}. Друзей можно позвать ссылкой.`)
      : h(Hint, null, 'Нет связи с сервером присутствия — позвать можно ссылкой.'),
    h(Hint, { cls: 'hint mt' }, `Состояние: ${status === 'connected' ? 'подключено' : 'переподключение…'}`));
}
export function lobbyHtml(r: Room, myPid: PlayerId | null, status: NetStatus, presence: PresenceSnapshot | null = null, myId = '', unread = 0): string {
  return toString(h(Lobby, { r, myPid, status, presence, myId, unread }));
}

// ---------- подготовка ----------
export function setupWaitV(G: Game, corr = false): JSX.Element {
  const waiting = G.players.filter((x) => !setupDone(x)).map((x) => x.name);
  return h(Pass, null,
    h(Sub, null, 'Подготовка'), h('div', { class: 'big' }, 'Готово!'),
    h('div', null, `Ждём: ${waiting.join(', ') || '…'}`),
    corr ? h(Sub, null, 'Можно закрыть игру: когда дойдёт твой ход, придёт уведомление.') : null,
    h(Btn, { cls: 'ghost', id: 'btn-leave', c: { k: 'leave' } }, corr ? '‹ К партиям' : 'Выйти'));
}

/** Партия по переписке: с экранов подготовки можно уйти к списку партий — она подождёт */
const CorrBack = ({ corr }: { corr?: boolean }): V =>
  corr ? h(Btn, { cls: 'ghost back corr-back', id: 'btn-leave', c: { k: 'leave' } }, '‹ К партиям') : null;
export const setupWaitHtml = (G: Game): string => toString(setupWaitV(G));

export function setupPassV(p: Player, third = false): JSX.Element {
  const what = third ? 'он выбирает третьего фамильяра из ничейных (жетон «три фамильяра»)' : 'он выбирает жетон свойства, а затем фамильяра';
  return h(Pass, null,
    h(Sub, null, 'Подготовка'), h('div', { class: 'big' }, p.name),
    h('div', null, `Передай телефон этому игроку — ${what}.`),
    h(Btn, { cls: 'primary big-btn', id: 'btn-pass', c: { k: 'setupPass' } }, `Я ${p.name}, готов`));
}
export const setupPassHtml = (p: Player, third = false): string => toString(setupPassV(p, third));

/** Идёт ли у игрока вторая фаза подготовки (третий фамильяр) */
export function inThirdPhase(G: Game, pid: PlayerId): boolean {
  return !!G.setup && (G.setup.third ?? []).includes(pid);
}

/** Жетон «три фамильяра»: оба предложенных плюс ничейный третий */
export function threeFamiliars(s: SetupUi): boolean {
  return !!s.traitId && !!H.traitPassive({ id: s.pid, name: '', bot: false, wizard: null, traitId: s.traitId, familiars: [], life: 0, chips: 0, loser: false, deck: [], hand: [], discard: [], inPlay: [], permanents: [], ddk: [], handSize: 0 })?.threeFamiliars;
}

/** Шаг подготовки с v11: сначала жетон (фамильяры скрыты), затем фамильяр; до v11 — всё сразу ('both') */
export function setupStep(G: Game, pid: PlayerId): 'trait' | 'familiar' | 'both' {
  if (!stepwiseSetup(G)) return 'both';
  return player(G, pid).traitId ? 'familiar' : 'trait';
}

/** Готов ли выбор текущего шага подготовки (при «трёх фамильярах» остаются оба, выбирать фамильяра нечего) */
export function setupReady(G: Game, s: SetupUi): boolean {
  const step = setupStep(G, s.pid);
  if (step === 'trait') return !!s.traitId;
  if (step === 'familiar') return !!s.familiar;
  if (!s.traitId) return false;
  return threeFamiliars(s) || !!s.familiar;
}

const DoneBtn = ({ enabled, label = 'Готово' }: { enabled: boolean; label?: string }): V =>
  h('div', { class: 'mt' }, h(Btn, { cls: 'primary big-btn', id: 'btn-setup-done', c: { k: 'setupDone' }, disabled: !enabled }, label));

const TraitMedal = ({ tid, sel, pick }: { tid: string; sel: boolean; pick: boolean }): V =>
  // жетон-медальон: свой текст, не карта из базы — потому не Card
  h('div', { class: cx('setup-card token-medal', sel && 'sel'), ...(pick ? cmdProps({ k: 'trait', id: tid }) : {}) },
    h('div', { class: 'medal-in' }, h('div', { class: 'medal-title' }, `Жетон свойства${isTraitRebalanced(tid) ? ' ⚖' : ''}`), h(Txt, { cls: 'cardtext', text: traitDef(tid).text })));

export function SetupChoose({ G, s, corr = false }: { G: Game; s: SetupUi; corr?: boolean }): V {
  const p = player(G, s.pid);
  if (!G.setup) throw new Error('Подготовка уже завершена');
  const offers = G.setup.traitOffers[s.pid] ?? [];
  const fams = G.setup.familiarOffers[s.pid] ?? [];
  const step = setupStep(G, s.pid);
  const famList = (three: boolean): V => h('div', { class: cx('setup-grid', (three || s.familiar) && 'has-sel') }, fams.map((cid) =>
    cardV({ cid }, { variant: 'setup', selected: three || s.familiar === cid, tapCmd: { k: 'fam', cid } })));
  if (step === 'familiar' && p.traitId) {
    // жетон уже лежит перед игроком — теперь (и только теперь) видны фамильяры
    return h(Screen, null,
      h(CorrBack, { corr }),
      h('h1', null, `${p.name}: подготовка`),
      h('h2', null, '1. Жетон колдунского свойства'),
      h('div', { class: 'setup-grid has-sel' }, h(TraitMedal, { tid: p.traitId, sel: true, pick: false })),
      h('h2', null, '2. Фамильяр'),
      h(Hint, null, 'Выбери одного из двух — второй уходит в коробку.'),
      famList(false),
      h(DoneBtn, { enabled: setupReady(G, s) }));
  }
  if (step === 'trait') {
    return h(Screen, null,
      h(CorrBack, { corr }),
      h('h1', null, `${p.name}: подготовка`),
      h('h2', null, '1. Жетон колдунского свойства'),
      h(Hint, null, 'Выбери один из двух (второй уходит в коробку). Фамильяров откроешь после — по правилам сначала жетон.'),
      h('div', { class: cx('setup-grid', s.traitId && 'has-sel') }, offers.map((tid) => h(TraitMedal, { tid, sel: s.traitId === tid, pick: true }))),
      h(DoneBtn, { enabled: setupReady(G, s), label: 'Взять жетон' }));
  }
  // партия до v11: жетон и фамильяр одним выбором
  const three = threeFamiliars(s);
  return h(Screen, null,
    h('h1', null, `${p.name}: подготовка`),
    h('h2', null, '1. Жетон колдунского свойства'),
    h(Hint, null, 'Выбери один из двух (второй уходит в коробку).'),
    h('div', { class: cx('setup-grid', s.traitId && 'has-sel') }, offers.map((tid) => h(TraitMedal, { tid, sel: s.traitId === tid, pick: true }))),
    h('h2', null, '2. Фамильяр'),
    h(Hint, null, three ? 'Твой жетон: оставь себе обоих. Третьего — любого ничейного — выберешь, когда выберут все.' : 'Выбери одного из двух.'),
    famList(three),
    h(DoneBtn, { enabled: setupReady(G, s) }));
}
export function setupChooseHtml(G: Game, s: SetupUi): string {
  return toString(h(SetupChoose, { G, s }));
}

/** Вторая фаза подготовки: третий фамильяр из ничейных (все уже выбрали — пул окончательный) */
export function setupThirdV(G: Game, s: SetupUi, corr = false): JSX.Element {
  const p = player(G, s.pid);
  if (!G.setup) throw new Error('Подготовка уже завершена');
  const free = G.setup.freeFamiliars;
  return h(Screen, null,
    h(CorrBack, { corr }),
    h('h1', null, `${p.name}: третий фамильяр`),
    // оба предложенных фамильяра игрок получил вместе с жетоном и шага фамильяра не видел — показываем их здесь
    p.familiars.length ? h(Fragment, null,
      h('h2', null, 'Уже твои'),
      h('div', { class: 'mrow' }, p.familiars.map((c) => cardV({ cid: c.cid }, { cls: 'sm', zone: 'view' })))) : null,
    h('h2', null, 'Третий — из ничейных'),
    h(Hint, null, 'Жетон «три фамильяра»: все выбрали, вот кто остался ничейным — возьми любого.'),
    h('div', { class: 'mrow' },
      free.map((cid) => cardV({ cid }, { cls: 'sm', selected: s.freeFam === cid, zone: 'freefam' })),
      free.length ? null : h(Hint, null, 'Ничейных фамильяров не осталось — остаются двое.')),
    h(DoneBtn, { enabled: !!s.freeFam || !free.length }));
}
export const setupThirdHtml = (G: Game, s: SetupUi): string => toString(setupThirdV(G, s));

// ---------- лента событий и передача телефона (хотсит) ----------
export function FeedItemV({ G, d, extraCls = '' }: { G: Game; d: FeedItem; extraCls?: string }): V {
  const cls = cx('feed-item', d.cls, d.minor && 'minor', d.mine && 'mine', extraCls);
  const row = h(Fragment, null,
    h('span', { class: 'feed-ic' }, d.icon),
    h('span', { class: 'feed-text' }, h(TextV, { G, text: d.text })),
    d.cid && !hasCardMark(d.text) ? h('span', { class: 'feed-card' }, h(CardChipV, { cid: d.cid })) : null);
  if (!d.sub?.length) return h('div', { class: cls, 'data-seq': d.seq }, row);
  // атака: заголовок с картой в строке, ниже — исходы
  return h('div', { class: cx(cls, 'feed-atk'), 'data-seq': d.seq },
    h('div', { class: 'feed-row' }, row),
    h('div', { class: 'feed-sub' }, d.sub.map((s) => h(FeedItemV, { G, d: s }))));
}
export function feedItemHtml(G: Game, d: FeedItem, extraCls = ''): string {
  return toString(h(FeedItemV, { G, d, extraCls }));
}

/** Экран «передай телефон» / «твой ход» игроку need; evs — сжатая лента того, что он ещё не видел;
 *  same — телефон уже у него (боты сходили, сводка перед его ходом), передавать никому не нужно */
export function PassScreen({ G, need, evs, same, unread = 0 }: { G: Game; need: PlayerId; evs: readonly FeedItem[]; same: boolean; unread?: number }): V {
  const p = player(G, need);
  const pr = G.prompt;
  const active = G.turn ? player(G, G.turn.player) : null;
  let kind = 'turn';
  let head = same ? 'Твой ход' : 'Передай телефон';
  let why = same ? 'боты сходили — вот что случилось' : 'его ход';
  let btnLabel = same ? 'Ходить' : `Я ${p.name}`;
  let extra: V = null;
  if (pr) {
    if (pr.type === 'defend') {
      kind = 'defend';
      const attacker = pr.attacker != null ? player(G, pr.attacker) : null;
      const cardName = pr.card ? card(pr.card).name : 'Беспредел';
      head = '⚔ Тебя атакуют!';
      why = `${attacker ? attacker.name + ' атакует' : 'Беспредел атакует'} картой «${cardName}» — дальше решишь, защищаться ли`;
      btnLabel = same ? 'К защите' : `Я ${p.name} — защищаюсь`;
      extra = h('div', { class: 'pass-card' }, pr.card
        ? cardV({ inst: pr.card }, { cls: 'sm', key: 'pass:atk', zone: 'view' })
        : h(Txt, { cls: 'cardtext small', text: pr.text }));
    } else if (pr.type === 'info') {
      kind = 'ask';
      head = same ? 'Сообщение' : 'Сообщение для';
      why = pr.title;
      if (same) btnLabel = 'Смотреть';
    } else {
      kind = 'ask';
      head = 'Нужно решение';
      why = `${active && active.id !== need ? `ход ${active.name} — ` : ''}${pr.title || (same ? 'нужно сделать выбор' : 'ему нужно сделать выбор')}`;
      if (same) btnLabel = 'Решать';
    }
  }
  return h(Pass, { cls: cx(`pass-${kind}`, evs.length > 0 && 'with-feed') },
    h(SocialBar, { unread, compact: true }),
    h(Sub, { cls: 'sub pass-head' }, head),
    h('div', { class: 'big' }, p.name),
    h(Sub, null, `${p.wizard ?? ''} · ${why}`),
    extra,
    evs.length ? h('div', { class: 'feed-wrap' },
      h('div', { class: 'feed-title' }, `${same ? 'Что произошло' : `Что произошло, пока ${p.name} не смотрел`}${evs.length > 6 ? ` (${evs.length})` : ''}`),
      h('div', { class: 'feed', id: 'feed' }, evs.map((d) => h(FeedItemV, { G, d })))) : null,
    h(Row, { cls: 'center' },
      h(Btn, { cls: cx('primary big-btn', kind === 'defend' && 'attack'), id: 'btn-pass', c: { k: 'pass' } }, btnLabel),
      h(Btn, { cls: 'ghost', id: 'btn-log', c: { k: 'log' } }, 'Журнал')));
}
export function passHtml(G: Game, need: PlayerId, evs: readonly FeedItem[], same = false): string {
  return toString(h(PassScreen, { G, need, evs, same }));
}

// ---------- конец ----------
/** Как партия сдвинула место в рейтинге: номинация, место до → после (и рейтинг Эло против людей) */
export function RatingChangeV({ rc }: { rc: RatingChange }): V {
  const nom = rc.cat === 'people' ? '🌐 против людей' : rc.level ? `⚔️ против ботов · ${levelEmoji(rc.level)} ${LEVEL_PLURAL[rc.level]}` : '⚔️ против ботов';
  const up = rc.rankBefore > 0 ? rc.rankBefore - rc.rankAfter : 0;
  const place = rc.rankBefore === 0
    ? h('span', null, 'Ты в таблице: ', h('b', null, `${rc.rankAfter}-е место`), ` из ${rc.total}`)
    : up === 0
      ? h('span', null, 'Место: ', h('b', null, rc.rankAfter), ` из ${rc.total} — без изменений`)
      : h('span', null, 'Место: ', `${rc.rankBefore} → `, h('b', null, rc.rankAfter),
          h('span', { class: cx('rc-delta', up > 0 ? 'up' : 'down') }, up > 0 ? ` ▲ ${up}` : ` ▼ ${-up}`), ` из ${rc.total}`);
  const dr = (rc.ratingAfter ?? 0) - (rc.ratingBefore ?? 0);
  return h(Panel, { cls: 'rating-change mt' },
    h('div', { class: 'rc-head' }, h('span', null, '🏆 Рейтинг'), h('span', { class: 'sub' }, nom)),
    place ? h('div', { class: 'rc-line' }, place) : null,
    rc.cat === 'people' && rc.ratingAfter != null
      ? h('div', { class: 'rc-line' }, 'Рейтинг: ', `${rc.ratingBefore ?? 1000} → `, h('b', null, rc.ratingAfter),
          dr ? h('span', { class: cx('rc-delta', dr > 0 ? 'up' : 'down') }, ` ${dr > 0 ? '+' : '−'}${Math.abs(dr)}`) : null)
      : null,
    h(GameAchV, { items: gameAchItems(rc) }),
    h(Btn, { cls: 'link', id: 'btn-end-top', c: { k: 'endToTop' } }, 'Открыть рейтинги'));
}

export const ratingChangeHtml = (rc: RatingChange): string => toString(h(RatingChangeV, { rc }));

export function EndScreen({ G, online, unread = 0, rating = null, ratingLoading = false, daily }: { G: Game; online: boolean | 'corr'; unread?: number; rating?: RatingChange | null; ratingLoading?: boolean; daily?: DailyState }): V {
  const winners = winnersOf(G).map((id) => player(G, id).name);
  const ei = G.endInfo;
  // разбор — только хотсит (у онлайн-представления нет журнала действий) и только когда за столом были люди
  const review = !online && (G.journal?.length ?? 0) > 0 && G.players.some((p) => !p.bot);
  return h(Screen, null,
    h(SocialBar, { unread }),
    h('h1', null, 'Игра окончена'),
    h(Panel, { cls: 'center' },
      h('div', { class: 'winner big' }, `🏆 ${winners.join(' и ') || '—'}`),
      winners.length > 1 ? h(Sub, { cls: 'sub mt' }, 'Полная ничья: поровну ПО, легенд и жетонов дохлых колдунов — победа на всех') : null,
      G.rebalance ? h(Sub, { cls: 'sub mt' }, '⚖️ Режим ребаланса') : null,
      G.mutators?.length ? h(Sub, { cls: 'sub mt' }, mutatorDefs(G).map((m) => `${m.emoji} ${m.name}`).join(' · ')) : null,
      ei?.reason ? h(Sub, { cls: 'sub mt' }, `Конец игры${ei.turn != null ? ` в ход ${player(G, ei.turn).name}` : ''}: ${ei.reason}`) : null),
    G.daily && daily ? h(DailyResultV, { G, d: daily }) : null,
    rating ? h(RatingChangeV, { rc: rating }) : ratingLoading ? h(Hint, { cls: 'hint center mt' }, '🏆 Считаем место в рейтинге…') : null,
    (G.scores ?? []).map((s) => {
      const p = player(G, s.pid);
      return h(Panel, { cls: 'mt' },
        h('b', null, p.name), ` (${p.wizard ?? ''}) — `, h('span', { class: 'winner' }, `${s.total} ПО`),
        h('table', { class: 'score' },
          s.lines.map((l) => h('tr', null, h('td', null, l.label), h('td', { class: 'num' }, `${l.value > 0 ? '+' : ''}${l.value}`))),
          h('tr', null, h('td', { class: 'sub' }, `легенд: ${s.legends}, ЖДК: ${s.ddk}`), h('td', null))));
    }),
    h(Row, { cls: 'mt' },
      online
        ? h(Btn, { cls: 'primary', id: 'btn-leave', c: { k: 'leave' } }, online === 'corr' ? '‹ К партиям по переписке' : 'Выйти из комнаты')
        : h(Btn, { cls: 'primary', id: 'btn-newgame', c: { k: 'toStart' } }, 'Новая партия'),
      review ? h(Btn, { cls: 'ghost', id: 'btn-review', c: { k: 'review' } }, '🔍 Разбор') : null,
      h(Btn, { cls: 'ghost', id: 'btn-log', c: { k: 'log' } }, 'Журнал')));
}
export function endHtml(G: Game, online: boolean): string {
  return toString(h(EndScreen, { G, online }));
}
